import BaseComponent from '@/components/base'
import type { SelectedOption } from '@/types/shopify'
import gsap from '@/core/gsap'
import { prefersReducedMotion } from '@/core/utils/a11y'

interface VariantPickerOptionSettings {
  onChange?: () => void
}

export default class VariantPickerOption extends BaseComponent {
  static TYPE = 'variant-picker-option'

  settings: VariantPickerOptionSettings
  name: string | undefined
  select: HTMLSelectElement | null
  inputs: HTMLInputElement[]
  marker: HTMLElement | null = null
  markerWrapper: HTMLElement | null = null

  constructor(el: HTMLElement, options: VariantPickerOptionSettings = {}) {
    super(el, { watchResize: true })

    this.settings = {
      ...options
    }

    this.name = this.dataset.name

    if (!this.name) {
      console.warn('No name attribute found')
    }

    // Picker options are either <select> tags or a series of <input> tags
    this.select = this.qs('select') as HTMLSelectElement || null
    this.inputs = this.qsa('input') as HTMLInputElement[]

    this.el.addEventListener('change', this.onChange.bind(this))

    this.setupSwatchMarker()
  }

  // For swatch (colour) options, inject a single ring marker that slides between
  // swatches on change rather than a static per-swatch outline.
  setupSwatchMarker() {
    const firstSwatch = this.qs('.swatch') as HTMLElement | null
    if (!firstSwatch) return // not a swatch option — sizes keep the filled box

    const wrapper = firstSwatch.closest('.composable-input')?.parentElement as HTMLElement | null
    if (!wrapper) return

    // Isolate the wrapper so the marker's z-index resolves against the swatches
    // only — it always paints above them, never behind (swatches are position:relative).
    wrapper.style.position = 'relative'
    wrapper.style.isolation = 'isolate'
    this.markerWrapper = wrapper

    this.marker = document.createElement('span')
    this.marker.className = 'swatch-marker'
    this.marker.setAttribute('aria-hidden', 'true')
    wrapper.appendChild(this.marker)

    // Position once layout has settled, without animating the first paint.
    requestAnimationFrame(() => this.positionMarker(false))
  }

  positionMarker(animate = true, activeInput?: HTMLInputElement) {
    if (!this.marker || !this.markerWrapper) return

    // Prefer the input that actually fired the change; fall back to the first
    // checked (initial paint / resize).
    const checked = activeInput ?? this.inputs.find(input => input.checked)
    const swatch = checked?.nextElementSibling?.querySelector('.swatch') as HTMLElement | null

    if (!swatch) {
      gsap.set(this.marker, { autoAlpha: 0 })
      return
    }

    // Measure against the wrapper via bounding rects — robust regardless of which
    // ancestor is the swatch's offsetParent.
    const gap = 3 // reproduces the old outline-offset ring
    const wrapRect = this.markerWrapper.getBoundingClientRect()
    const swatchRect = swatch.getBoundingClientRect()

    gsap.set(this.marker, {
      width: swatchRect.width + gap * 2,
      height: swatchRect.height + gap * 2
    })

    const dest = {
      x: swatchRect.left - wrapRect.left - gap,
      y: swatchRect.top - wrapRect.top - gap,
      autoAlpha: 1
    }

    if (animate && !prefersReducedMotion()) {
      gsap.to(this.marker, { ...dest, duration: 0.45, ease: 'expo.out', overwrite: true })
    }
    else {
      gsap.set(this.marker, dest)
    }
  }

  onResize() {
    this.positionMarker(false)
  }

  destroy() {
    if (this.marker) gsap.killTweensOf(this.marker)
    super.destroy()
  }

  get selectedOption(): SelectedOption | undefined {
    let name: string | undefined
    let value: string | undefined

    if (this.select) {
      name = this.select.name
      value = this.select.value
    }
    else {
      const selectedInput = this.inputs.find(input => input.checked)

      if (selectedInput) {
        name = selectedInput.name
        value = selectedInput.value
      }
    }

    return name && value ? { name, value } : undefined
  }

  updateValueAvailability(value: string, available: boolean) {
    if (this.select) {
      ([...this.select.children] as HTMLOptionElement[]).forEach(option => {
        if (option.value === value) {
          option.disabled = !available
        }
      })
    }
    else {
      const input = this.inputs.find(input => input.value === value)

      if (!input) return

      // Keep sold-out options selectable so the add-to-cart button can show
      // "Sold Out" — flag them instead of disabling.
      input.toggleAttribute('data-sold-out', !available)
    }
  }

  onChange(e?: Event) {
    const target = e?.target as HTMLInputElement | undefined
    this.positionMarker(true, target?.checked ? target : undefined)
    this.settings.onChange?.()
  }
}